import { PropsWithChildren } from 'react';
import { ArrowContainer, Popover } from 'react-tiny-popover';
import classNames from 'classnames/bind';
import {
  TooltipDirection,
  TooltipProps,
  TooltipSize,
  TooltipTheme,
  directionToPositions,
  ValueOf,
} from './Tooltip.types';
import { useTooltipVisibility } from './useTooltipVisibility';

import styles from './Tooltip.pcss';

const cx = classNames.bind(styles);

const directionToAlign = (direction: ValueOf<typeof TooltipDirection>): 'start' | 'center' | 'end' => {
  switch (direction) {
    case TooltipDirection.TOP_LEFT:
    case TooltipDirection.BOTTOM_LEFT:
      return 'end';
    case TooltipDirection.BOTTOM_RIGHT:
      return 'start';
    default:
      return 'center';
  }
};

const arrowColorMap: Record<ValueOf<typeof TooltipTheme>, string> = {
  dark: 'var(--neutral-950)',
  light: 'var(--neutral-50)',
};

const getArrowColor = (theme: ValueOf<typeof TooltipTheme>): string =>
  arrowColorMap[theme] ?? arrowColorMap[TooltipTheme.DARK];

const Tooltip = ({
  children,
  tooltip,
  size = TooltipSize.AUTO,
  contentSize = TooltipSize.AUTO,
  direction = TooltipDirection.RIGHT,
  theme = TooltipTheme.DARK,
  delay = true,
  isOpen: isManuallyOpen,
  isHiddenOnFocus = false,
  wrapperClassName,
}: PropsWithChildren<TooltipProps>): JSX.Element => {
  const { isVisible, setIsVisible, handleMouseEnter, handleMouseLeave, handleFocusCapture, handleBlurCapture } =
    useTooltipVisibility(delay, isHiddenOnFocus);

  if (!tooltip && isManuallyOpen === undefined) {
    /* eslint-disable-next-line react/jsx-no-useless-fragment */
    return <>{children}</>;
  }

  const shouldShow = (isManuallyOpen ?? isVisible) && !!tooltip;

  return (
    <Popover
      isOpen={shouldShow}
      positions={directionToPositions[direction]}
      align={directionToAlign(direction)}
      onClickOutside={() => setIsVisible(false)}
      containerClassName="z-[10000]"
      containerStyle={{ pointerEvents: 'none' }}
      content={({ position, childRect, popoverRect }) => (
        <ArrowContainer
          position={position}
          childRect={childRect}
          popoverRect={popoverRect}
          arrowColor={getArrowColor(theme)}
          arrowSize={5}
        >
          <div className={cx('body-sm', 'tooltipContent', contentSize, theme)} data-test-id="tooltip-content">
            {tooltip}
          </div>
        </ArrowContainer>
      )}
    >
      <div
        className={cx('min-w-0', size, wrapperClassName)}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onFocusCapture={isHiddenOnFocus ? handleFocusCapture : undefined}
        onBlurCapture={isHiddenOnFocus ? handleBlurCapture : undefined}
        data-test-id="tooltip"
      >
        {children}
      </div>
    </Popover>
  );
};

export default Tooltip;
